// ./src/hooks/useThemes.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  Theme,
  ThemeService,
  ThemeFilter,
  ThemeInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseThemesOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<ThemeFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;

  userId?: string | null;
}

export interface UseThemesReturn {
  // Data state
  theme: Theme | null;
  themes: Theme[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<ThemeFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingThemeId: string | null;
  selectedTheme: Theme | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<ThemeFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<Theme[]>;
  create: (params: ThemeInput) => Promise<Theme | null>;
  update: (id: string, params: ThemeInput) => Promise<Theme | null>;
  remove: (id: string) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<ThemeFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedTheme: (theme: Theme | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  themesByStatus: Record<string, Theme[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useThemes = (options: UseThemesOptions = {}): UseThemesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [theme, setTheme] = useState<Theme | null>(null);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<ThemeFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingThemeId, setRemovingThemeId] = useState<string | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<Theme | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => themes.length === 0 && !loading,
    [themes.length, loading]
  );

  const themesByStatus = useMemo(() => {
    return themes.reduce((acc, theme) => {
      const status = String(theme.status || "unknown");
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(theme);
      return acc;
    }, {} as Record<string, Theme[]>);
  }, [themes]);

  // Helper function to update theme in list if it exists, or add it if not
  const updateOrAddThemeInList = useCallback((updatedTheme: Theme) => {
    setThemes((prev) => {
      const existingIndex = prev.findIndex((t) => t.id === updatedTheme.id);
      if (existingIndex >= 0) {
        const newThemes = [...prev];
        newThemes[existingIndex] = updatedTheme;
        return newThemes;
      } else {
        return [updatedTheme, ...prev];
      }
    });
  }, []);

  // Helper function to remove theme from list if it exists
  const removeThemeFromList = useCallback((themeId: string) => {
    setThemes((prev) => prev.filter((t) => t.id !== themeId));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setTheme(null);
    setThemes([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingThemeId(null);
    setSelectedTheme(null);
  }, []);

  // Get single theme (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await ThemeService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const themeData = result.data[0] as Theme;
          setTheme(themeData);
          updateOrAddThemeInList(themeData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load theme";
          setLastError(errorMessage);
          setTheme(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load theme";
        setLastError(errorMessage);
        setTheme(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddThemeInList]
  );

  // List themes (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<ThemeFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setThemes([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await ThemeService.list(
          filter as ThemeFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newThemes = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setThemes((prev) => {
              const existingIds = new Set(prev.map((t) => t.id));
              const uniqueNewThemes = newThemes.filter(
                (t) => !existingIds.has(t.id)
              );
              return [...prev, ...uniqueNewThemes];
            });
          } else {
            setThemes(newThemes);
          }

          setHasMore(newThemes.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load themes";
          setLastError(errorMessage);
          if (!isAppending) setThemes([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load themes";
        setLastError(errorMessage);
        if (!isAppending) setThemes([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many themes by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<Theme[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await ThemeService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedThemes = Array.isArray(result.data) ? result.data : [];
          loadedThemes.forEach((theme) => {
            updateOrAddThemeInList(theme);
          });
          setLastError(null);
          return loadedThemes;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load themes";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load themes";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddThemeInList]
  );

  // Create theme (matching API method name)
  const create = useCallback(
    async (params: ThemeInput): Promise<Theme | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await ThemeService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newTheme = result.data[0] as Theme;
          setTheme(newTheme);
          setThemes((prev) => [newTheme, ...prev]);
          setLastError(null);
          return newTheme;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create theme";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create theme";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update theme (matching API method name)
  const update = useCallback(
    async (id: string, params: ThemeInput): Promise<Theme | null> => {
      if (!id) {
        setLastError("Theme ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await ThemeService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedTheme = result.data[0] as Theme;
          setTheme(updatedTheme);
          updateOrAddThemeInList(updatedTheme);
          setLastError(null);
          return updatedTheme;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update theme";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update theme";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddThemeInList]
  );

  // Remove theme (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Theme ID is required for removal");
        return false;
      }

      setRemovingThemeId(id);
      setLastError(null);

      try {
        const result = await ThemeService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setTheme(null);
          removeThemeFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove theme";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove theme";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingThemeId(null);
      }
    },
    [removeThemeFromList]
  );

  // Load more themes
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh themes
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    theme,
    themes,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingThemeId,
    selectedTheme,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedTheme,
    reset,
    themesByStatus,
    lastError,
    clearError,
  };
};
